import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import AgentAuditCard from "@/components/ops/AgentAuditCard";
import ConvertedUsersDrilldown from "@/components/ops/ConvertedUsersDrilldown";
import { DEFAULT_ANALYTICS_RANGE } from "@/lib/dateDefaults";
import DatePicker from "@/components/common/DatePicker";

/** Dedicated /ops/agents/{agent_id} profile — full-width audit summary,
 *  filterable by date range, with a "See credited users" quick link that
 *  opens the same drill-down drawer used on the dashboard. */
export default function AgentProfilePage() {
  const { agentId } = useParams();
  const nav = useNavigate();
  const _r = DEFAULT_ANALYTICS_RANGE();
  const [dateFrom, setDateFrom] = useState(_r.from);
  const [dateTo, setDateTo] = useState(_r.to);
  const [drilldown, setDrilldown] = useState({ open: false });

  const filters = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  return (
    <div className="space-y-4" data-testid="agent-profile-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="agent-profile-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From" testId="agent-profile-from" />
          <span className="text-slate-400">→</span>
          <DatePicker value={dateTo} onChange={setDateTo} placeholder="To" testId="agent-profile-to" />
        </div>
      </div>

      <AgentAuditCard agentId={agentId} filters={filters} variant="full" />

      {/* Quick drill-down triggers */}
      <div className="glass-card p-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
          Audit conversions
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ["total", "All conversions"],
            ["call", "Call-credited"],
            ["sms", "SMS-credited"],
            ["wn", "WN-credited"],
            ["invalid", "Invalid-credited"],
          ].map(([kind, label]) => (
            <button
              type="button"
              key={kind}
              onClick={() => setDrilldown({
                open: true, kind,
                agent_id: agentId,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
              })}
              className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-700"
              data-testid={`agent-profile-drilldown-${kind}`}
            >
              {label} →
            </button>
          ))}
        </div>
      </div>

      <ConvertedUsersDrilldown
        state={drilldown}
        onClose={() => setDrilldown({ open: false })}
      />
    </div>
  );
}
